import React from 'react'
import { GlobalNavigationLayout } from '../../parts/navigations/GlobalNavigationLayout'
import { VStack } from '../../parts/bases/VStack'
import { HStack } from '../../parts/bases/HStack'
import { Center } from '../../parts/bases/Center'
import { Text } from '../../parts/bases/Text'
import Link from 'next/link'
import { PagePaths } from '@/src/utils/PagePaths'
import Image from 'next/image'
import { EnergyGauge } from '../../parts/commons/EnergyGauge'
import { Absolute } from '../../parts/bases/Absolute'
import inOperationBanner from '@/public/images/inOperationBanner.png'
import upcomingThumbnail from '@/public/images/upcoming_thumbnail.png'
import megasparkingFrame from '@/public/images/megasparking_02.png'
import megasparkingAM from '@/public/images/megasparking_03.png'
import { Spinner } from '../../parts/commons/Spinner'
import config from '@/tailwind.config'
import IconRefesh from '@/public/images/iconRefresh.png'
import { GameSelectModal } from './modals/GameSelectModal'
import { MegaSparkingLabel } from '../../parts/commons/MegaSparkingLabel'

type ArcadeMachineType = {
  id: string
  gameImageUrl: string
  amImageUrl: string
  energy: number
  maxEnergy: number
  extractedEnergy: number
  megaSparkUpcoming: boolean
  inOperation: boolean
  feverSparkRemain: number | null
}

export type Props = {
  games: Array<{ id: string; name: string }>
  selectedGame: string
  selectedGameTitle: string
  ams: Array<ArcadeMachineType>
  loading: boolean
  onClickBack: () => void
  visibleGameSelect: boolean
  onClickGameSelect: () => void
  onClickGameSelectClose: () => void
  onClickGameSelectAccept: (gameId: string) => void
}

export const Layout = (props: Props) => {
  return (
    <>
      <GlobalNavigationLayout visibleBack onClickBack={props.onClickBack} title="ArcadeMachine">
        <VStack className="p-2 pt-0">
          <HStack className="flex-wrap items-center justify-end gap-y-2 gap-x-1.5">
            <div
              onClick={props.onClickGameSelect}
              className="flex w-[120px] items-center justify-between gap-x-2 rounded-[3px] border border-solid border-akiverse-pink bg-akiverse-pink/50"
            >
              <Text className="py-1 pl-2 text-[12px] font-semibold text-white">
                {props.selectedGameTitle}
              </Text>
              <div className="flex items-center px-1">
                <Image src={IconRefesh} height={14} width={14} />
              </div>
            </div>
          </HStack>
          <HStack className="mt-2 w-full flex-1 flex-wrap">
            {!props.loading ? (
              props.ams.map((am) => <ArcadeMachineCell key={am.id} {...am} />)
            ) : (
              <Center className="mt-40 h-full w-full">
                <Spinner size={30} visible color={config.theme.extend.colors.neon.blue} />
              </Center>
            )}
          </HStack>
        </VStack>
      </GlobalNavigationLayout>

      <GameSelectModal
        visible={props.visibleGameSelect}
        selectedGame={props.selectedGame}
        onClickClose={props.onClickGameSelectClose}
        onClickAccept={props.onClickGameSelectAccept}
      />
    </>
  )
}

const ArcadeMachineCell = (props: ArcadeMachineType) => {
  const {
    id,
    gameImageUrl,
    amImageUrl,
    maxEnergy,
    energy,
    extractedEnergy,
    megaSparkUpcoming,
    inOperation,
    feverSparkRemain,
  } = props
  const isMegaSparking = feverSparkRemain !== null && feverSparkRemain !== undefined
  return (
    <Center className="aspect-square w-1/3 p-2">
      <Link href={PagePaths.arcademachineDetail({ arcadeMachineId: id })}>
        <a className="flex aspect-square w-full">
          <Center
            className="relative aspect-square w-full rounded-md bg-black bg-cover bg-center"
            style={{
              backgroundImage: `url('${gameImageUrl}')`,
              borderWidth: !megaSparkUpcoming && feverSparkRemain === null ? 1 : 0,
              borderColor: !megaSparkUpcoming && feverSparkRemain === null ? '#52369F' : '',
            }}
          >
            <Center className="relative h-full w-full rounded-md bg-black bg-opacity-60">
              <Image src={amImageUrl} width="95%" height={'95%'} alt="" />
              {isMegaSparking && (
                <Absolute className="w-full">
                  <Image src={megasparkingAM} className="h-full w-full" alt="" />
                </Absolute>
              )}
              <Absolute className="bottom-6 left-6">
                <EnergyGauge
                  height={40}
                  maxEnergy={maxEnergy}
                  energy={energy}
                  extractedEnergy={extractedEnergy}
                  feverSparkRemain={feverSparkRemain}
                />
              </Absolute>
            </Center>
            {isMegaSparking && (
              <Absolute className="mt-2 w-full">
                <MegaSparkingLabel feverSparkRemain={feverSparkRemain} size="M" />
              </Absolute>
            )}
            {inOperation && (
              <Absolute className="bottom-3 z-10 w-full bg-black/50">
                <Image src={inOperationBanner} className="object-contain" alt="" />
              </Absolute>
            )}
            {megaSparkUpcoming && (
              <Absolute className="z-10 h-[103%] w-[103%]">
                <Image src={upcomingThumbnail} className="h-full w-full" alt="" />
              </Absolute>
            )}
            {isMegaSparking && (
              <Absolute className="z-10 h-[108%] w-[108%]">
                <Image src={megasparkingFrame} className="h-full w-full" alt="" />
              </Absolute>
            )}
          </Center>
        </a>
      </Link>
    </Center>
  )
}
